// packages/shared-hooks/src/lead-activity/useLeadActivityFeed.ts
//
// `GET /leads/:leadId/activity` — cursor-paginated audit feed (#1085).
// Backend orders rows `occurredAt DESC, id DESC` and mints an opaque
// base64url cursor; the UI walks pages until `nextCursor === null`.
//
// WHY (#2317 D-8, rolling-safe): the default feed EXCLUDES the two
// `LEAD_TASK_CANCELLED`/`LEAD_TASK_REOPENED` audit kinds (#2319 backend). A
// caller opts in by passing `{ includeTaskCancelReopen: true }`, which sends
// the `X-Magimp-Lead-Activity-V2: task-cancel-reopen` header AND reads from a
// DISTINCT cache slot (`feedV2`). The DEFAULT call (opts absent/false) sends
// NO header and keeps the existing `feed` key byte-compatible, so pre-existing
// single-arg callers behave exactly as before.
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import {
  LeadActivityFeedResponseSchema,
  LEAD_ACTIVITY_V2_HEADER,
  LEAD_ACTIVITY_V2_TASK_CANCEL_REOPEN,
  type LeadActivityFeedResponse,
} from '@magimp/shared';
import { useApiClient } from '../api/context.js';
import { leadActivityKeys } from './keys.js';

export interface UseLeadActivityFeedOptions {
  /** Opt into the #2319 rolling-safe task cancel/reopen audit rows (#2317 D-8). */
  includeTaskCancelReopen?: boolean;
}

export function useLeadActivityFeed(leadId: string | null, opts: UseLeadActivityFeedOptions = {}) {
  const client = useApiClient();
  const enabled = !!leadId;
  const v2 = opts.includeTaskCancelReopen === true;
  const headers = v2
    ? { [LEAD_ACTIVITY_V2_HEADER]: LEAD_ACTIVITY_V2_TASK_CANCEL_REOPEN }
    : undefined;
  return useInfiniteQuery<
    LeadActivityFeedResponse,
    Error,
    InfiniteData<LeadActivityFeedResponse>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: v2 ? leadActivityKeys.feedV2(leadId ?? '') : leadActivityKeys.feed(leadId ?? ''),
    enabled,
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) =>
      client.get<LeadActivityFeedResponse>(
        `/leads/${leadId}/activity`,
        { signal, query: pageParam ? { cursor: pageParam } : {}, ...(headers ? { headers } : {}) },
        LeadActivityFeedResponseSchema,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
